import { CalendarClock, Plus } from 'lucide-react';
import TeeBadge from '@/components/ui/tee-badge';
import { TeeButton } from '@/components/ui/tee-button';
import type { GamePlanActivity } from '@/types/gamePlanTracker';
import { ACTIVITY_TYPES, shortDate } from './activityType.config';

const microLabel =
  'text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-content-sub-title)]';

interface ActivityStreamProps {
  week: number;
  logs: GamePlanActivity[];
  isCurrentWeek: boolean;
  onLog: () => void;
}

// The week's logged activities as a stream of per-type styled cards.
export function ActivityStream({
  week,
  logs,
  isCurrentWeek,
  onLog,
}: ActivityStreamProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className={microLabel}>Logged · Week {week}</span>
        {logs.length > 0 && (
          <span className="text-[12px] text-[var(--text-content-sub-title)]">
            {logs.length} {logs.length === 1 ? 'activity' : 'activities'}
          </span>
        )}
      </div>

      {logs.length === 0 ? (
        <div className="flex flex-col items-start gap-2.5 py-1">
          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[var(--background-page-header)]">
            <CalendarClock size={18} className="text-[var(--text-content-sub-title)]" />
          </div>
          <p className="text-body-s text-[var(--text-content-sub-title)]">
            No activity logged {isCurrentWeek ? 'this week' : 'for this week'} yet.
            Visits, calls and events show up here and count toward your to-dos.
          </p>
          <TeeButton
            variant="secondary"
            size="s"
            text="Log activity"
            leftIcon={<Plus size={15} />}
            onClick={onLog}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {logs.map((log) => {
            const style = ACTIVITY_TYPES[log.category] ?? ACTIVITY_TYPES.other;
            const Icon = style.Icon;
            const line = [log.type, log.outcome, shortDate(log.date)]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={log.id}
                className="rounded-[10px] bg-[var(--background-card)] p-3 flex gap-3 shadow-[0_1px_3px_rgba(12,61,45,0.08)]"
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-none"
                  style={{ background: style.tint }}
                >
                  <Icon size={17} style={{ color: style.accent }} />
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-body-s font-semibold text-[var(--text-content-title)] truncate">
                      {log.org || log.name || style.label}
                    </span>
                    <TeeBadge label={style.label} type="grey" size="s" />
                  </div>
                  {line && (
                    <span className="text-[12px] text-[var(--text-content-sub-title)]">
                      {line}
                    </span>
                  )}
                  {log.nextStep && (
                    <span className="text-[12px] text-[var(--text-content-title)] mt-0.5">
                      <span className="text-[var(--text-content-sub-title)]">Next:</span>{' '}
                      {log.nextStep}
                    </span>
                  )}
                  {log.followUp && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--color-text-gold)] mt-1">
                      <CalendarClock size={12} /> Follow up {shortDate(log.followUp)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
